// Path: /src/components/admin/Users/VerifyUsers.tsx
// Updated to default to active users (isDisabled: false) on load
// Retains Tuition Status and Evaluation Status columns, and enhances filters
// Supports navigation to aEvaluationS.tsx for admin evaluation flow

import React, { useEffect, useState, useCallback } from "react";
import { withFirebase, FirebaseContextType } from "../../dev/Firebase/Firebase";
import { Table, Tag, Tabs, Select, Button } from "antd";
import { userDocToUser } from "../../dev/utils/userHelper";
import { collection, getDocs } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";

const { Option } = Select;

interface User {
  key: string;
  userRole?: string;
  verificationStatus: string;
  name?: string;
  loc?: string;
  isDisabled: boolean;
  submittedForVerification: boolean;
  evaluationStatus?: string;
  tuitionFees?: {
    introPaymentStatus?: "Pending" | "Completed";
    initiPaymentStatus?: "Pending" | "Completed";
  };
  [key: string]: any;
}

interface VerifyUsersProps {
  firebase: FirebaseContextType;
}

const VerifyUsersContainer = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  border-radius: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #3552fc;
    color: #fff;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  margin-right: 8px;
  font-weight: bold;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    margin-right: 8px;
    transition: all 0.3s ease;
  }
  .ant-tabs-tab:hover {
    background: #f0f0f0;
  }
  .ant-tabs-tab-active {
    background: #1890ff !important;
    border-color: #1890ff !important;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #ffffff !important;
  }
  .ant-tabs-tab-btn {
    color: #000000;
    padding: 8px 16px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const VerifyUsers: React.FC<VerifyUsersProps> = ({ firebase }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "disabled">("active"); // Default to active
  const [userTypeFilter, setUserTypeFilter] = useState<"all" | "uOLSS" | "uOLSME" | "uOLSLM">("all");
  const [tuitionFilter, setTuitionFilter] = useState<"all" | "pendingTuition" | "completedTuition">("all");
  const [evaluationFilter, setEvaluationFilter] = useState<"all" | "evaluated" | "notEvaluated">("all");
  const [activeTab, setActiveTab] = useState<string>("active"); // Default to active users
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersRef = collection(firebase.firestore, "users");
        const userDocs = await getDocs(usersRef);
        const usersList: User[] = userDocs.docs.map((doc) => userDocToUser(doc));
        setUsers(usersList);
        console.log("Fetched users for verification:", usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to fetch users: " + (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [firebase]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter") || "active"; // Default to active if no param
    if (
      filterParam === "all" ||
      filterParam === "active" ||
      filterParam === "disabled" ||
      filterParam === "pending" ||
      filterParam === "verified" ||
      filterParam === "not-verified" ||
      filterParam === "pendingTuition" ||
      filterParam === "completedTuition" ||
      filterParam === "pendingEvaluation"
    ) {
      setActiveTab(filterParam);
      if (filterParam === "pendingTuition") {
        setTuitionFilter("pendingTuition");
        setStatusFilter("all");
      } else if (filterParam === "completedTuition") {
        setTuitionFilter("completedTuition");
        setStatusFilter("all");
      } else if (filterParam === "pendingEvaluation") {
        setEvaluationFilter("notEvaluated");
        setStatusFilter("all");
      } else {
        setStatusFilter(filterParam === "active" ? "active" : filterParam === "disabled" ? "disabled" : "all");
        setTuitionFilter("all");
        setEvaluationFilter("all");
      }
    } else {
      // Set default URL param to active
      navigate(`/admin/users/verify?filter=active`, { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    let filtered: User[] = users;

    // Apply tab filter
    if (activeTab === "pending") {
      filtered = users.filter((user) => user.submittedForVerification);
    } else if (activeTab === "verified") {
      filtered = users.filter((user) => user.verificationStatus === "Verified");
    } else if (activeTab === "not-verified") {
      filtered = users.filter((user) => user.verificationStatus === "Not Verified");
    } else if (activeTab === "pendingTuition") {
      filtered = users.filter((user) =>
        user.tuitionFees?.introPaymentStatus === "Pending" || user.tuitionFees?.initiPaymentStatus === "Pending"
      );
    } else if (activeTab === "completedTuition") {
      filtered = users.filter((user) =>
        user.tuitionFees?.introPaymentStatus === "Completed" && user.tuitionFees?.initiPaymentStatus === "Completed"
      );
    } else if (activeTab === "pendingEvaluation") {
      filtered = users.filter((user) => user.evaluationStatus !== "Evaluated");
    } else if (activeTab === "active") {
      filtered = users.filter((user) => !user.isDisabled);
    }

    // Apply status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((user) => !user.isDisabled);
    } else if (statusFilter === "disabled") {
      filtered = filtered.filter((user) => user.isDisabled);
    }

    // Apply user type filter
    if (userTypeFilter !== "all") {
      const normalizedFilter = userTypeFilter.toLowerCase();
      filtered = filtered.filter((user) => {
        const userRole = user.userRole?.toLowerCase();
        if (normalizedFilter === "uolss") return userRole === "uolss" || userRole === "uolss_col";
        if (normalizedFilter === "uolsme") return userRole === "uolsme" || userRole === "olsme_col";
        if (normalizedFilter === "uolslm") return userRole === "uolslm" || userRole === "olslm_col";
        return false;
      });
    }

    // Apply tuition filter
    if (tuitionFilter !== "all") {
      if (tuitionFilter === "pendingTuition") {
        filtered = filtered.filter((user) =>
          user.tuitionFees?.introPaymentStatus === "Pending" || user.tuitionFees?.initiPaymentStatus === "Pending"
        );
      } else if (tuitionFilter === "completedTuition") {
        filtered = filtered.filter((user) =>
          user.tuitionFees?.introPaymentStatus === "Completed" && user.tuitionFees?.initiPaymentStatus === "Completed"
        );
      }
    }

    // Apply evaluation filter
    if (evaluationFilter !== "all") {
      if (evaluationFilter === "evaluated") {
        filtered = filtered.filter((user) => user.evaluationStatus === "Evaluated");
      } else if (evaluationFilter === "notEvaluated") {
        filtered = filtered.filter((user) => user.evaluationStatus !== "Evaluated");
      }
    }

    setFilteredUsers(filtered);
    console.log("Filtered users after applying filters:", {
      statusFilter,
      userTypeFilter,
      tuitionFilter,
      evaluationFilter,
      activeTab,
      filtered,
    });
  }, [users, statusFilter, userTypeFilter, tuitionFilter, evaluationFilter, activeTab]);

  const handleStatusFilterChange = (value: "all" | "active" | "disabled") => {
    setStatusFilter(value);
    setActiveTab(value === "active" ? "active" : value === "disabled" ? "disabled" : "all");
    navigate(`/admin/users/verify?filter=${value}`);
  };

  const handleUserTypeFilterChange = (value: "all" | "uOLSS" | "uOLSME" | "uOLSLM") => {
    setUserTypeFilter(value);
  };

  const handleTuitionFilterChange = (value: "all" | "pendingTuition" | "completedTuition") => {
    setTuitionFilter(value);
    setActiveTab(value === "all" ? "active" : value); // Default to active if all
    navigate(`/admin/users/verify?filter=${value === "all" ? "active" : value}`);
  };

  const handleEvaluationFilterChange = (value: "all" | "evaluated" | "notEvaluated") => {
    setEvaluationFilter(value);
    setActiveTab(value === "all" ? "active" : value === "notEvaluated" ? "pendingEvaluation" : "active");
    navigate(`/admin/users/verify?filter=${value === "all" ? "active" : value === "notEvaluated" ? "pendingEvaluation" : "active"}`);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setTuitionFilter("all");
    setEvaluationFilter("all");
    setStatusFilter(key === "active" ? "active" : "all");
    navigate(`/admin/users/verify?filter=${key}`);
  };

  const handleNavigateToDetails = useCallback(
    (user: User) => {
      console.log("Navigating to user details with state:", user);
      navigate(`/admin/user/${user.key}`, { state: { user } });
    },
    [navigate]
  );

  const handleNavigateToEvaluation = useCallback(
    (user: User) => {
      console.log("Navigating to evaluation for user:", user);
      navigate(`/admin/evaluation/${user.key}`, { state: { user } });
    },
    [navigate]
  );

  const columns = [
    {
      title: "Id",
      dataIndex: "key",
      sorter: (a: User, b: User) => (a.key > b.key ? 1 : -1),
    },
    {
      title: "User type",
      dataIndex: "userRole",
      sorter: (a: User, b: User) => (a.userRole || "") > (b.userRole || "") ? 1 : -1,
    },
    {
      title: "Verification status",
      dataIndex: "verificationStatus",
      sorter: (a: User, b: User) => (a.verificationStatus > b.verificationStatus ? 1 : -1),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: User, b: User) => (a.name || "") > (b.name || "") ? 1 : -1,
    },
    {
      title: "Location",
      dataIndex: "loc",
      sorter: (a: User, b: User) => (a.loc || "") > (b.loc || "") ? 1 : -1,
    },
    {
      title: "Status",
      dataIndex: "isDisabled",
      sorter: (a: User, b: User) => (a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1),
      render: (_: any, record: User) =>
        record.isDisabled ? <Tag color="red">Disabled</Tag> : <Tag color="blue">Active</Tag>,
    },
    {
      title: "Tuition Status",
      dataIndex: "tuitionFees",
      sorter: (a: User, b: User) => {
        const aStatus = `${a.tuitionFees?.introPaymentStatus || "Pending"}/${a.tuitionFees?.initiPaymentStatus || "Pending"}`;
        const bStatus = `${b.tuitionFees?.introPaymentStatus || "Pending"}/${b.tuitionFees?.initiPaymentStatus || "Pending"}`;
        return aStatus > bStatus ? 1 : -1;
      },
      render: (_: any, record: User) => {
        const introStatus = record.tuitionFees?.introPaymentStatus || "Pending";
        const initiStatus = record.tuitionFees?.initiPaymentStatus || "Pending";
        return (
          <span>
            Intro: <Tag color={introStatus === "Completed" ? "green" : "orange"}>{introStatus}</Tag>
            Initi: <Tag color={initiStatus === "Completed" ? "green" : "orange"}>{initiStatus}</Tag>
          </span>
        );
      },
    },
    {
      title: "Evaluation Status",
      dataIndex: "evaluationStatus",
      sorter: (a: User, b: User) => (a.evaluationStatus || "Not Evaluated") > (b.evaluationStatus || "Not Evaluated") ? 1 : -1,
      render: (_: any, record: User) => {
        const evalStatus = record.evaluationStatus || "Not Evaluated";
        return <Tag color={evalStatus === "Evaluated" ? "green" : "red"}>{evalStatus}</Tag>;
      },
    },
    {
      title: "Action",
      dataIndex: "",
      render: (_: any, user: User) => (
        <ActionButtons>
          <Button onClick={() => handleNavigateToDetails(user)}>Details</Button>
          <Button type="primary" onClick={() => handleNavigateToEvaluation(user)}>
            Evaluate
          </Button>
        </ActionButtons>
      ),
    },
  ];

  const tabItems = [
    { key: "all", label: "All Users" },
    { key: "active", label: "Active Users" }, // Promoted for visibility
    { key: "pending", label: "Pending Verification" },
    { key: "verified", label: "Verified" },
    { key: "not-verified", label: "Not Verified" },
    { key: "pendingTuition", label: "Pending Tuition" },
    { key: "completedTuition", label: "Completed Tuition" },
    { key: "pendingEvaluation", label: "Pending Evaluation" },
  ];

  if (loading) {
    console.log("VerifyUsers rendering - loading state");
    return <div>Loading users...</div>;
  }

  return (
    <VerifyUsersContainer>
      <Helmet>
        <title>Verify Users - One Light System OLS Admin</title>
        <meta
          name="description"
          content="Verify active user accounts in the One Light System OLS admin panel for Sun Light Meditation education. Join OLS at olsme.com/OLSStudentProspect."
        />
        <meta
          name="keywords"
          content="OLS admin verification, Sun Light Meditation, active users, join OLS"
        />
        <link rel="canonical" href="https://www.olsme.com/admin/users/verify" />
      </Helmet>
      {error && <div style={{ color: "red", marginBottom: 16 }}>{error}</div>}
      <StyledTabs items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
      <FilterContainer>
        <div>
          <FilterLabel>Filter by status:</FilterLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            style={{ width: 200 }}
            aria-label="Filter users by status"
          >
            <Option value="all">All users</Option>
            <Option value="active">Active users</Option>
            <Option value="disabled">Disabled users</Option>
          </Select>
        </div>
        <div>
          <FilterLabel>Filter by user type:</FilterLabel>
          <Select
            value={userTypeFilter}
            onChange={handleUserTypeFilterChange}
            style={{ width: 200 }}
            aria-label="Filter users by type"
          >
            <Option value="all">All types</Option>
            <Option value="uOLSS">uOLSS</Option>
            <Option value="uOLSME">uOLSME</Option>
            <Option value="uOLSLM">uOLSLM</Option>
          </Select>
        </div>
        <div>
          <FilterLabel>Filter by tuition status:</FilterLabel>
          <Select
            value={tuitionFilter}
            onChange={handleTuitionFilterChange}
            style={{ width: 200 }}
            aria-label="Filter users by tuition status"
          >
            <Option value="all">All</Option>
            <Option value="pendingTuition">Pending Payments</Option>
            <Option value="completedTuition">Completed Payments</Option>
          </Select>
        </div>
        <div>
          <FilterLabel>Filter by evaluation status:</FilterLabel>
          <Select
            value={evaluationFilter}
            onChange={handleEvaluationFilterChange}
            style={{ width: 200 }}
            aria-label="Filter users by evaluation status"
          >
            <Option value="all">All</Option>
            <Option value="evaluated">Evaluated</Option>
            <Option value="notEvaluated">Not Evaluated</Option>
          </Select>
        </div>
      </FilterContainer>
      {filteredUsers.length === 0 ? (
        <div>No users match the current filters. Try adjusting the tab or filters.</div>
      ) : (
        <StyledTable
          columns={columns}
          dataSource={filteredUsers}
          onChange={(pagination, filters, sorter) => console.log("Table params:", pagination, filters, sorter)}
        />
      )}
    </VerifyUsersContainer>
  );
};

export default withFirebase(VerifyUsers);